import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateCashier, type CashierProvisioned } from '@/hooks/use-employees';
import { useT } from '@/i18n/use-t';
import { OneTimePinModal } from './one-time-pin-modal';

type Translate = (key: string, bakedEnglish?: string) => string;

function buildSchema(t: Translate) {
  return z.object({
    employeeCode: z
      .string()
      .min(2, t('employees.new.errMinCode', 'min 2 characters'))
      .max(32, t('employees.new.errMaxCode', 'max 32 characters'))
      .regex(
        /^[A-Za-z0-9_-]+$/,
        t('employees.new.errCodeCharset', 'alphanumeric / underscore / dash only'),
      ),
    loginName: z
      .string()
      .trim()
      .max(120, t('employees.new.errMax120', 'max 120 characters'))
      .optional(),
    displayName: z
      .string()
      .min(1, t('employees.new.errRequired', 'required'))
      .max(120, t('employees.new.errMax120', 'max 120 characters')),
    pin: z
      .string()
      .regex(/^\d{4,8}$/, t('employees.new.errPin', 'PIN must be 4..8 digits'))
      .optional()
      .or(z.literal('')),
    generatePin: z.boolean(),
  });
}
type FormValues = z.infer<ReturnType<typeof buildSchema>>;

export function CashierNewPage() {
  const t = useT();
  const schema = buildSchema(t);
  const navigate = useNavigate();
  const create = useCreateCashier();
  const [provisioned, setProvisioned] = useState<CashierProvisioned | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { generatePin: true, pin: '' },
  });

  const generatePin = watch('generatePin');

  async function onSubmit(values: FormValues): Promise<void> {
    setSubmitError(null);
    try {
      const result = await create.mutateAsync({
        employeeCode: values.employeeCode,
        loginName:
          values.loginName === undefined || values.loginName === '' ? null : values.loginName,
        displayName: values.displayName,
        pin: values.generatePin || values.pin === '' ? undefined : values.pin,
      });
      setProvisioned(result);
    } catch (err) {
      setSubmitError(extractError(err, t));
    }
  }

  return (
    <div className="max-w-xl space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">{t('employees.new.title', 'New cashier')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('employees.new.introBefore', 'The cleartext PIN is shown')}{' '}
          <strong>{t('employees.pin.exactlyOnce', 'exactly once')}</strong>{' '}
          {t(
            'employees.new.introAfter',
            'after create. Hand it to the cashier physically — losing it requires a re-provision.',
          )}
        </p>
      </header>

      <form
        className="space-y-4 rounded-card border border-border bg-card p-5"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <div className="space-y-2">
          <Label htmlFor="employeeCode">{t('employees.field.employeeCode', 'Employee code')}</Label>
          <Input
            id="employeeCode"
            placeholder={t('employees.new.codePlaceholder', 'e.g. 001 / A-203 / badge-7')}
            autoComplete="off"
            {...register('employeeCode')}
          />
          {errors.employeeCode !== undefined && (
            <p className="text-xs text-destructive">{errors.employeeCode.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="loginName">{t('employees.field.loginName', 'QR login name')}</Label>
          <Input
            id="loginName"
            placeholder={t('employees.new.loginNamePlaceholder', 'e.g. 0000001857')}
            autoComplete="off"
            {...register('loginName')}
          />
          <p className="text-xs text-muted-foreground">
            {t(
              'employees.new.loginNameHelp',
              'Exact GP cashierloginname encoded in the cashier QR badge. Must be unique when set.',
            )}
          </p>
          {errors.loginName !== undefined && (
            <p className="text-xs text-destructive">{errors.loginName.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="displayName">{t('employees.field.displayName', 'Display name')}</Label>
          <Input
            id="displayName"
            placeholder={t(
              'employees.new.displayNamePlaceholder',
              'Surfaces on receipts and the terminal lock screen',
            )}
            autoComplete="off"
            {...register('displayName')}
          />
          {errors.displayName !== undefined && (
            <p className="text-xs text-destructive">{errors.displayName.message}</p>
          )}
        </div>

        <div className="space-y-3 rounded-md border border-border bg-secondary/40 p-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" {...register('generatePin')} className="size-4" />
            {t('employees.new.generatePin', 'Generate a random 4-digit PIN')}
          </label>
          {!generatePin && (
            <div className="space-y-2">
              <Label htmlFor="pin">{t('employees.new.pinLabel', 'PIN (4-8 digits)')}</Label>
              <Input
                id="pin"
                type="text"
                inputMode="numeric"
                pattern="\d{4,8}"
                placeholder={t('employees.new.pinPlaceholder', 'e.g. 1234')}
                autoComplete="off"
                {...register('pin')}
              />
              {errors.pin !== undefined && (
                <p className="text-xs text-destructive">{errors.pin.message}</p>
              )}
            </div>
          )}
        </div>

        {submitError !== null && <p className="text-sm text-destructive">{submitError}</p>}

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? t('employees.new.creating', 'Creating…')
              : t('employees.new.submit', 'Create + provision PIN')}
          </Button>
          <Button asChild variant="outline" type="button">
            <Link to="/employees">{t('common.cancel', 'Cancel')}</Link>
          </Button>
        </div>
      </form>

      {provisioned !== null && (
        <OneTimePinModal
          open={true}
          pin={provisioned.pin}
          cashierLabel={`${provisioned.cashier.displayName} (${provisioned.cashier.employeeCode})`}
          onAcknowledge={() => {
            const id = provisioned.cashier.id;
            setProvisioned(null);
            navigate(`/employees/${id}`);
          }}
        />
      )}
    </div>
  );
}

function extractError(err: unknown, t: Translate): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { message?: string | string[] } | undefined;
    if (typeof data?.message === 'string') return data.message;
    if (Array.isArray(data?.message)) return data.message.join('; ');
    return err.message;
  }
  return t('employees.error.unexpected', 'Unexpected error');
}
